import BottomSheetHeader from '@components/BottomSheetHeader';
import Button from '@components/Button';
import Column from '@components/Column';
import Map from '@components/Map';
import Row from '@components/Row';
import Text from '@components/Text';
import TextUnderline from '@components/TextUnderline';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useCopyShopAddress from '@hooks/shop/useCopyShopAddress';
import useShop from '@hooks/shop/useShop';
import useAppCustom from '@hooks/useAppCustom';
import Copy from '@icons/Copy';
import PhoneFill from '@icons/phone/PhoneFill';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType, useFlow } from '@stackflow/react';
import { openExternalLink } from '@utils/nativeUtils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import WarningIcon from './WarningIcon';

interface Params {
    shopId: string;
    lat?: number;
    lng?: number;
}

declare module '@stackflow/config' {
    interface Register {
        ShopLocationBottomSheet: Params;
    }
}

const ShopLocationBottomSheet:ActivityComponentType<'ShopLocationBottomSheet'> = ({ params }) => {
    const {
        shopId, lat, lng,
    } = params;
    const { t } = useTranslation('shopLocation');
    const shop = useShop(shopId);
    const { pop, push } = useFlow();
    const copyAddress = useCopyShopAddress(shop?.address);
    const nameWords : string[] = useMemo(() => shop?.name.match(new RegExp(`.{1,${16}}`, 'g')) || [], [shop]);
    const handleOrderButtonClick = () => {
        pop();
        push('ItemsActivity', { shopId, type: 'ALL' });
    };
    const { usePhoneNumber } = useAppCustom().shop;
    return (
        <BottomSheet>
            <BottomSheetHeader/>
            <Column fullWidth align='center' spacing={20}>
                {shop && (
                    <Map center={{ lat: lat!, lng: lng! }}
                        myLocation={{ lat: lat!, lng: lng! }}
                        width='100%'
                        height={218}
                        markers={[
                            {
                                lat: lat!, lng: lng!, name: shop.name, id: shop.id, isMyShop: false,
                            },
                        ]} />
                )}
                <Contents align='center'>
                    {nameWords.map((word, index) => (
                        <TextUnderline key={index}>
                            <NameText size='3xl' fontWeight='bold'>
                                {word}
                            </NameText>
                        </TextUnderline>
                    ))}
                    <Text size='3xl'>{t('title')}</Text>
                </Contents>
                <Column align='center'>
                    <AddressContainer spacing={8} onClick={copyAddress} >
                        <AddressText color={colors.gray6} size='lg' align='center'>
                            {shop?.address}
                        </AddressText>
                        <Copy color={colors.gray6} size={16}/>
                    </AddressContainer>
                    {(shop?.phoneNumber && usePhoneNumber) && (
                        <Row spacing={8}
                            onClick={() => openExternalLink(`tel:${shop.phoneNumber}`)}>
                            <Text color={colors.gray6} size='lg'>
                                {shop.phoneNumber}
                            </Text>
                            <PhoneFill color={colors.gray6} size={16}/>
                        </Row>
                    )}
                </Column>
                <Footer fullWidth
                    spacing={30} >
                    <Warning align='center'
                        onClick={pop} >
                        <Row>
                            <WarningIcon/>
                            <Text>{t('warning.title')}</Text>
                        </Row>
                        <Text align='center'>{t('warning.description')}</Text>
                    </Warning>
                    <Row fullWidth spacing={8}>
                        <Button variant='secondary' onClick={pop} css={css`flex: 1; padding: 0;`}>
                            {t('button.selectOtherShop')}
                        </Button>
                        <Button css={css`flex: 1; padding: 0;`}
                            disabled={shop?.orderStatus !== 'OPEN'}
                            onClick={handleOrderButtonClick} >
                            {shop?.orderStatus === 'OPEN' ? t('button.order') : t('button.preparing')}
                        </Button>
                    </Row>
                </Footer>
            </Column>
        </BottomSheet>
    );
};

const Contents = styled(Column)`
    padding: 0 20px;
`;

const NameText = styled(Text)`
    font-size: 24px;
`;

const AddressContainer = styled(Row)`
    padding: 0 20px;
`;

const AddressText = styled(Text)`
    flex: 1;
`;

const Footer = styled(Column)`
    padding: 0 20px 30px;
    position: relative;
`;

const Warning = styled(Column)`
    background-color: var(--color-gray1);
    border: 1px solid #CB2B2B;
    border-radius: 6px;
    min-width: 100%;
    padding: 10px 0;
`;

export default ShopLocationBottomSheet;
